import React from 'react';
import { Form, Formik, Field } from 'formik';

import { useToast } from 'helpers/hooks';
import { Avatar } from 'components/Avatar';
import { Input } from 'components/Input';
import { ProfileFormBody } from 'components/ProfileFormBody';
import { ReactComponent as EditIcon } from 'assets/svg/edit.svg';
import { request } from 'helpers/tools';
import styles from 'css_modules/User.module.css';

export const User = ({ setUser, user = {} }) => {
    const showToast = useToast();

    const bodyProps = {
        button: {
            text: 'Uložit změny',
            primary: true
        }
    };

    const handleImageUpload = (event, setFieldValue) => {
        const file = event.target.files?.[0];
        if (file) {
            setFieldValue('avatar', file);
        }
    };

    const sanitizeErrors = (errorBody) => {
        if (!errorBody || typeof errorBody !== 'object') return {};
        
        const safeErrors = {};
        Object.entries(errorBody).forEach(([key, val]) => {
            if (typeof val === 'string') {
                safeErrors[key] = val;
            } else if (Array.isArray(val)) {
                safeErrors[key] = val
                    .map(item => (typeof item === 'object' && item !== null ? item.title || item.detail || JSON.stringify(item) : item))
                    .join(', ');
            } else if (typeof val === 'object' && val !== null) {
                safeErrors[key] = val.title || val.detail || val.message || JSON.stringify(val);
            } else {
                safeErrors[key] = String(val);
            }
        });
        return safeErrors;
    };

    return (
        <Formik
            initialValues={{
                first_name: user.first_name || '',
                last_name: user.last_name || '',
                username: user.username || '',
                email: user.email || '',
                telephone: user.telephone || '',
                id: user.pk || '',
                company_title: user.company_title || '',
                country: typeof user.country === 'object' ? (user.country?.title || '') : (user.country || ''),
                display_notifications: user.display_notifications ? 'true' : 'false',
                legacy_frontend: user.legacy_frontend ? 'true' : 'false'
            }}
            onSubmit={async (values, { setErrors }) => {
                const data = new FormData();
                const formValues = { ...values };

                if (!user.is_admin) {
                    formValues.company_title = user.company_title || '';
                }

                Object.entries(formValues).forEach(([key, val]) => {
                    if (val !== null && val !== undefined) {
                        data.append(key, val);
                    }
                });

                try {
                    const url = '/rest-auth/user/';
                    const response = await request(url, 'PATCH', data, false, true);

                    if (response?.ok) {
                        const updatedUser = response.body || {};
                        if (typeof updatedUser.client_company === 'object') {
                            updatedUser.client_company = updatedUser.client_company?.id ?? updatedUser.client_company?.pk;
                        }

                        setUser(updatedUser);
                        showToast('Změny byly uloženy');
                        window.location.reload();
                    } else {
                        const safeErrors = sanitizeErrors(response?.body);
                        setErrors(safeErrors);
                    }
                } catch (err) {
                    console.error('Failed to update user profile:', err);
                }
            }}
        >
            {({ setFieldValue }) => (
                <Form className={styles.form}>
                    <label className={styles.avatarUpload}>
                        <input
                            name="avatar"
                            type="file"
                            accept="image/*"
                            onChange={event => handleImageUpload(event, setFieldValue)}
                            hidden
                        />
                        <div className={styles.avatarContainer}>
                            <Avatar user={user} />
                        </div>
                        <EditIcon className={styles.editIcon} />
                        <span className={styles.avatarUploadText}>Změnit profilové foto</span>
                    </label>

                    <ProfileFormBody {...bodyProps}>
                        <Input name="first_name" autoComplete="given-name" required>
                            Jméno
                        </Input>
                        <Input name="last_name" autoComplete="family-name" required>
                            Příjmení
                        </Input>
                        <Input name="username" autoComplete="username" required>
                            Uživatelské jméno
                        </Input>
                        <Input name="telephone" type="tel" autoComplete="tel">
                            Telefon
                        </Input>
                        <Input name="email" type="email" autoComplete="email" required>
                            Email
                        </Input>
                        <Input name="id" disabled>
                            ID uživatele
                        </Input>
                        <Input name="country" autoComplete="country" disabled>
                            Země
                        </Input>
                        <Input name="company_title" autoComplete="organization" disabled={!user.is_admin}>
                            Název společnosti
                        </Input>

                        <br />
                        <div style={{ marginTop: '0rem' }}>
                            <div style={{ fontWeight: 'bold', color: 'rgb(178, 176, 189)', marginBottom: '0.3rem' }}>
                                Nové uživatelské rozhraní
                            </div>
                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Field type="radio" name="legacy_frontend" value="false" />
                                    <span>Ano</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Field type="radio" name="legacy_frontend" value="true" />
                                    <span>Ne</span>
                                </label>
                            </div>
                        </div>

                        <div style={{ marginTop: '0rem' }}>
                            <div style={{ fontWeight: 'bold', color: 'rgb(178, 176, 189)', marginBottom: '0.3rem' }}>
                                Upozornění
                            </div>
                            <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Field type="radio" name="display_notifications" value="true" />
                                    <span>Ano</span>
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <Field type="radio" name="display_notifications" value="false" />
                                    <span>Ne</span>
                                </label>
                            </div>
                        </div>
                    </ProfileFormBody>
                </Form>
            )}
        </Formik>
    );
};